/**
 * Shared, data-type-aware operator model used by dashboard filters *and* chart
 * conditional formatting (SSP-4176 / SSP-3620).
 *
 * The operator identifiers match Cube.js filter operators so the same value can
 * be sent as a filter payload or evaluated client-side against a single value.
 *
 * Labels are plain strings — consumers must wrap them in `translate()`.
 */
// ---------------------------------------------------------------------------
// Operator catalogue
// ---------------------------------------------------------------------------
const COMMON_SET_OPERATORS = [
    { value: 'notSet', labelKey: 'Blank', valueKind: 'none' },
    { value: 'set', labelKey: 'Not blank', valueKind: 'none' },
];
const STRING_OPERATORS = [
    { value: 'equals', labelKey: 'Equals', valueKind: 'single' },
    { value: 'notEquals', labelKey: 'Not equals', valueKind: 'single' },
    { value: 'contains', labelKey: 'Contains', valueKind: 'single' },
    { value: 'notContains', labelKey: 'Not contains', valueKind: 'single' },
    ...COMMON_SET_OPERATORS,
];
const NUMBER_OPERATORS = [
    { value: 'equals', labelKey: 'Equals', valueKind: 'single' },
    { value: 'notEquals', labelKey: 'Not equals', valueKind: 'single' },
    { value: 'gt', labelKey: 'Greater than', valueKind: 'single' },
    { value: 'gte', labelKey: 'Greater than or equal', valueKind: 'single' },
    { value: 'lt', labelKey: 'Less than', valueKind: 'single' },
    { value: 'lte', labelKey: 'Less than or equal', valueKind: 'single' },
    ...COMMON_SET_OPERATORS,
];
const TIME_OPERATORS = [
    { value: 'equals', labelKey: 'Equals', valueKind: 'single' },
    { value: 'notEquals', labelKey: 'Not equals', valueKind: 'single' },
    { value: 'afterDate', labelKey: 'After', valueKind: 'single' },
    { value: 'afterOrOnDate', labelKey: 'On or after', valueKind: 'single' },
    { value: 'beforeDate', labelKey: 'Before', valueKind: 'single' },
    { value: 'beforeOrOnDate', labelKey: 'On or before', valueKind: 'single' },
    { value: 'inDateRange', labelKey: 'In date range', valueKind: 'range' },
    { value: 'notInDateRange', labelKey: 'Not in date range', valueKind: 'range' },
    ...COMMON_SET_OPERATORS,
];
const BOOLEAN_OPERATORS = [{ value: 'equals', labelKey: 'Equals', valueKind: 'single' }];
/** The single source of truth for operator options per field type. */
export const FIELD_TYPE_OPERATORS = {
    string: STRING_OPERATORS,
    number: NUMBER_OPERATORS,
    time: TIME_OPERATORS,
    boolean: BOOLEAN_OPERATORS,
};
/**
 * Backwards-compatible shape kept for existing consumers that expect
 * `{ value, labelKey }[]`. Prefer `FIELD_TYPE_OPERATORS` in new code.
 */
export const OPERATORS_BY_FIELD_TYPE = Object.fromEntries(Object.entries(FIELD_TYPE_OPERATORS).map(([type, ops]) => [
    type,
    ops.map(({ value, labelKey }) => ({ value, labelKey })),
]));
// ---------------------------------------------------------------------------
// Field-type helpers
// ---------------------------------------------------------------------------
/** Normalise a raw type string (from a dataset or column config) to a canonical field type. */
export const toCanonicalFieldType = (rawType) => {
    const normalized = (rawType || '').trim().toLowerCase();
    switch (normalized) {
        case 'number':
        case 'numeric':
        case 'integer':
        case 'float':
        case 'decimal':
            return 'number';
        case 'time':
        case 'date':
        case 'datetime':
        case 'timestamp':
            return 'time';
        case 'boolean':
        case 'bool':
            return 'boolean';
        default:
            return 'string';
    }
};
export const isNumericFieldType = (fieldType) => toCanonicalFieldType(fieldType) === 'number';
export const isTimeFieldType = (fieldType) => toCanonicalFieldType(fieldType) === 'time';
// ---------------------------------------------------------------------------
// Operator helpers
// ---------------------------------------------------------------------------
export const getOperatorsForFieldType = (fieldType) => FIELD_TYPE_OPERATORS[toCanonicalFieldType(fieldType)];
export const getOperatorConfig = (fieldType, operator) => {
    if (!operator) {
        return undefined;
    }
    return getOperatorsForFieldType(fieldType).find((op) => op.value === operator);
};
export function getOperatorLabel(fieldType, operator) {
    return getOperatorConfig(fieldType, operator)?.labelKey;
}
/** Free-text operators expect a user-typed substring rather than a value pick from the dataset. */
const FREE_TEXT_OPERATORS = new Set(['contains', 'notContains', 'startsWith', 'endsWith']);
export const isFreeTextOperator = (operator) => !!operator && FREE_TEXT_OPERATORS.has(operator);
const RANGE_OPERATORS = new Set(['inDateRange', 'notInDateRange']);
export const isRangeOperator = (operator) => !!operator && RANGE_OPERATORS.has(operator);
const BLANK_OPERATORS = new Set(['set', 'notSet']);
export const isBlankOperator = (operator) => !!operator && BLANK_OPERATORS.has(operator);
/** True when the operator does not require any value input. */
export const operatorRequiresValue = (operator) => !isBlankOperator(operator);
// ---------------------------------------------------------------------------
// Legacy operator migration
// ---------------------------------------------------------------------------
/**
 * Legacy conditional-formatting rules used the generic comparison operators
 * `<`, `<=`, `>`, `>=`, `=`, `!=`. Existing rules must keep rendering after we
 * migrate to the canonical operator names — this map handles that translation.
 */
const LEGACY_OPERATOR_MAP = {
    '<': { number: 'lt', time: 'beforeDate', string: 'lt', boolean: 'lt' },
    '<=': { number: 'lte', time: 'beforeOrOnDate', string: 'lte', boolean: 'lte' },
    '>': { number: 'gt', time: 'afterDate', string: 'gt', boolean: 'gt' },
    '>=': { number: 'gte', time: 'afterOrOnDate', string: 'gte', boolean: 'gte' },
    '=': { number: 'equals', time: 'equals', string: 'equals', boolean: 'equals' },
    '!=': { number: 'notEquals', time: 'notEquals', string: 'notEquals', boolean: 'notEquals' },
};
/**
 * Translate a legacy operator symbol (e.g. `<`) to the canonical name for the
 * given field type. Returns the input unchanged when it's already canonical.
 */
export const normalizeOperator = (operator, fieldType) => {
    if (!operator) {
        return operator ?? '';
    }
    const canonicalFieldType = toCanonicalFieldType(fieldType);
    const mapped = LEGACY_OPERATOR_MAP[operator]?.[canonicalFieldType];
    return mapped ?? operator;
};
const toNumber = (value) => {
    if (value === null || value === undefined || value === '') {
        return NaN;
    }
    const num = typeof value === 'string' ? parseFloat(value) : Number(value);
    return num;
};
const toDateMs = (value) => {
    if (value === null || value === undefined || value === '') {
        return NaN;
    }
    const ms = new Date(value).getTime();
    return Number.isFinite(ms) ? ms : NaN;
};
const isBlankValue = (value) => value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
const compareNumbers = (actual, rule, op) => {
    if (Number.isNaN(actual) || Number.isNaN(rule)) {
        return false;
    }
    switch (op) {
        case 'equals':
            return actual === rule;
        case 'notEquals':
            return actual !== rule;
        case 'gt':
            return actual > rule;
        case 'gte':
            return actual >= rule;
        case 'lt':
            return actual < rule;
        case 'lte':
            return actual <= rule;
        default:
            return false;
    }
};
/**
 * Evaluate whether a condition matches a value, using the semantics of the
 * canonical operators. Legacy operators (`<`, `>`, ...) are transparently
 * migrated first so existing conditional formatting rules keep working.
 */
export const evaluateCondition = (actualValue, operator, ruleValue, fieldType) => {
    if (!operator) {
        return false;
    }
    const canonical = normalizeOperator(operator, fieldType);
    const canonicalFieldType = toCanonicalFieldType(fieldType);
    if (canonical === 'set') {
        return !isBlankValue(actualValue);
    }
    if (canonical === 'notSet') {
        return isBlankValue(actualValue);
    }
    if (canonical === 'inDateRange' || canonical === 'notInDateRange') {
        const actualMs = toDateMs(actualValue);
        const fromMs = toDateMs(ruleValue.from);
        const toMs = toDateMs(ruleValue.to);
        if (Number.isNaN(actualMs) || Number.isNaN(fromMs) || Number.isNaN(toMs)) {
            return false;
        }
        const inRange = actualMs >= fromMs && actualMs <= toMs;
        return canonical === 'inDateRange' ? inRange : !inRange;
    }
    if (canonicalFieldType === 'time') {
        const actualMs = toDateMs(actualValue);
        const ruleMs = toDateMs(ruleValue.singleValue);
        if (Number.isNaN(actualMs) || Number.isNaN(ruleMs)) {
            return false;
        }
        switch (canonical) {
            case 'equals':
                return actualMs === ruleMs;
            case 'notEquals':
                return actualMs !== ruleMs;
            case 'afterDate':
                return actualMs > ruleMs;
            case 'afterOrOnDate':
                return actualMs >= ruleMs;
            case 'beforeDate':
                return actualMs < ruleMs;
            case 'beforeOrOnDate':
                return actualMs <= ruleMs;
            default:
                return false;
        }
    }
    if (canonicalFieldType === 'string') {
        const actualStr = actualValue === null || actualValue === undefined ? '' : String(actualValue);
        const ruleStr = ruleValue.singleValue === undefined || ruleValue.singleValue === null ? '' : String(ruleValue.singleValue);
        const actualLower = actualStr.toLowerCase();
        const ruleLower = ruleStr.toLowerCase();
        switch (canonical) {
            case 'equals':
                return actualStr === ruleStr;
            case 'notEquals':
                return actualStr !== ruleStr;
            case 'contains':
                return actualLower.includes(ruleLower);
            case 'notContains':
                return !actualLower.includes(ruleLower);
            case 'startsWith':
                return actualLower.startsWith(ruleLower);
            case 'endsWith':
                return actualLower.endsWith(ruleLower);
            default:
                break;
        }
    }
    // Numeric / boolean comparisons — coerce to numbers, matching legacy behaviour
    // where "401" > "400" was a numeric compare rather than lexicographic.
    const actualNum = toNumber(actualValue);
    const ruleNum = toNumber(ruleValue.singleValue);
    if (canonical === 'equals' ||
        canonical === 'notEquals' ||
        canonical === 'gt' ||
        canonical === 'gte' ||
        canonical === 'lt' ||
        canonical === 'lte') {
        return compareNumbers(actualNum, ruleNum, canonical);
    }
    return false;
};
